import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';
import * as Util from '../../../common/utilities';

import {
  DataTable,
  DataTableColumn,
  DataTableCell,
  Input,
  InputIcon
} from '@salesforce/design-system-react';

import './DataExtensionList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  switch (property) {
    default: {
      return <td className="de-cell">{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class DataExtensionList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      lastSearch: '',
      initialLoad: false
    };
  }
  headerCell(property, formatMessage) {
    return formatMessage({ id: `components.ecs.dataExtensions.${property}` });
  }

  columns() {
    return {
      name: { sortable: false }
    };
  }

  tableData(items) {
    return (items || []).map(item => ({
      ...item,
      createdAt: formatTimestamp(this.props.intl)(item.created_date),
      id: item.customer_key
    }));
  }

  renderHeader() {
    return (
      <div className="data-extension-header">
        <h2>
          <FormattedMessage id="components.ecs.dataExtensions.header" />
        </h2>
        <div className="data-extension-info">
          <FormattedMessage id="components.ecs.dataExtensions.info" />
        </div>
      </div>
    );
  }

  search() {
    this.setState({ lastSearch: this.state.search });
    this.props.actions.getDataExtensions(this.state.search);
  }

  handleKeyPress(event) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  clear() {
    this.setState({ search: '' });
    const hasLastSearch = this.state.lastSearch && this.state.lastSearch.length;
    if (hasLastSearch) {
      this.search();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.onInitialLoad && !this.state.initialLoad) {
      if (
        Util.propChanged(
          prevProps,
          this.props,
          'dataExtensions.isLoading',
          false
        )
      ) {
        this.setState({ initialLoad: true });
        this.props.onInitialLoad();
      }
    }
  }

  selection() {
    const items = this.tableData(
      this.props.dataExtensions.data.data_extensions
    );
    const selection = items.filter(
      item =>
        this.props.selectedDataExtension &&
        item.customer_key === this.props.selectedDataExtension.customer_key
    );
    if (selection.length) {
      return selection;
    }
    return [];
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    const selection = items.filter(
      item =>
        this.props.selectedDataExtension &&
        item.customer_key === this.props.selectedDataExtension.customer_key
    );
    return (
      <DataTable
        items={items}
        fixedLayout
        className="data-extension-table"
        selection={selection}
        selectRows={this.props.rowSelectionMode}
        onRowChange={this.props.onDataExtensionSelect}
      >
        {Object.entries(this.columns()).map((column, index) => {
          const sortable = column[1].sortable;
          const property = column[0];
          return (
            <DataTableColumn key={index} sortable={sortable}>
              <CustomDataTableCell
                property={property}
                formatMessage={formatMessage}
                label={this.headerCell(property, formatMessage)}
              />
            </DataTableColumn>
          );
        })}
      </DataTable>
    );
  }

  render() {
    const { dataExtensions } = this.props;
    const items = this.tableData(dataExtensions.data.data_extensions);
    const clearButton = (
      <InputIcon
        assistiveText={{
          icon: 'Clear'
        }}
        name="clear"
        category="utility"
        onClick={() => this.clear()}
      />
    );
    const hasSearch = this.state.search && this.state.search.length > 0;
    const className = this.props.className || '';
    return (
      <div
        className={`slds-card-wrapper min-height-x-large card-bg ${className}`}
      >
        {this.renderHeader()}
        <Input
          hasSpinner={dataExtensions.isLoading}
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search'
              }}
              name="search"
              category="utility"
            />
          }
          iconRight={hasSearch ? clearButton : null}
          className="de-search-box"
          value={this.state.search}
          onChange={(evt, { value }) => this.setState({ search: value })}
          onKeyPress={this.handleKeyPress.bind(this)}
        />
        <div className="data-extension-table-container">
          {this.renderTable(items)}
        </div>
      </div>
    );
  }
}

DataExtensionList.contextTypes = {
  router: PropTypes.object
};

DataExtensionList.propTypes = {
  actions: PropTypes.object,
  className: PropTypes.string,
  dataExtensions: PropTypes.shape({
    data: PropTypes.shape({
      data_extensions: PropTypes.array
    }),
    isLoading: PropTypes.bool.isRequired
  }).isRequired,
  selectedDataExtension: PropTypes.object,
  onDataExtensionSelect: PropTypes.func.isRequired,
  onInitialLoad: PropTypes.func,
  rowSelectionMode: PropTypes.string,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(DataExtensionList);
